import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import { Upload, Settings, Users, Building2, User, ChevronDown, Menu, X } from 'lucide-react';



export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut, isPlatformAdmin, isCompanyAdmin } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    await signOut();
    setDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Don't show header on auth pages
  if (location?.pathname === '/login' || location?.pathname === '/signup') {
    return null;
  }

  // Don't show header if not authenticated
  if (!user) {
    return null;
  }

  return (
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Company */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center">
              <Building2 className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <div className="text-lg font-bold text-gray-900">PriceIQ</div>
                <div className="text-xs text-gray-500">{userProfile?.role === 'company_admin' ? 'Admin' : 'User'}</div>
              </div>
            </Link>
          </div>

          {/* Navigation Links */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/dashboard" className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
              Dashboard
            </Link>
            <Link to="/file-upload-dashboard" className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
              Upload
            </Link>
            <Link to="/results-dashboard" className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
              Results
            </Link>
            <Link to="/export-and-reports-center" className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
              Reports
            </Link>
            {isPlatformAdmin ? (
              <Link to="/system-management-console" className="text-purple-700 hover:text-purple-800 px-3 py-2 text-sm font-medium transition-colors bg-purple-50 rounded-md">
                System Console
              </Link>
            ) : (
              <Link to="/user-management" className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors">
                Users
              </Link>
            )}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {userProfile?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium">
                    {userProfile?.full_name || user?.email}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {dropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setDropdownOpen(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 border border-gray-200">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm text-gray-500">Signed in as</p>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.email}
                        </p>
                        {userProfile?.role && (
                          <p className="text-xs text-blue-600 capitalize">
                            {userProfile.role.replace('_', ' ')}
                          </p>
                        )}
                      </div>
                      <a
                        href="/user-profile-settings-center"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Settings
                      </a>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <a
                  href="/auth/login"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                >
                  Login
                </a>
                <a
                  href="/auth/signup"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
                >
                  Sign Up
                </a>
              </div>
            )}

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-600 hover:text-gray-900 focus:outline-none"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              <Link to="/dashboard" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                Dashboard
              </Link>
              <Link to="/file-upload-dashboard" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                Upload
              </Link>
              <Link to="/results-dashboard" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                Results
              </Link>
              <Link to="/export-and-reports-center" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                Reports
              </Link>
              {isPlatformAdmin ? (
                <Link to="/system-management-console" className="text-purple-700 hover:text-purple-800 px-3 py-2 text-sm font-medium bg-purple-50 rounded-md" onClick={() => setMobileMenuOpen(false)}>
                  System Console
                </Link>
              ) : (
                <Link to="/user-management" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                  Users
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}